/* eslint-disable @typescript-eslint/no-unused-vars */
import { useState } from 'react';
import { EPerPages, ISortOptions } from 'models/table';
import { IPaginator } from 'models/paginator';
import {
  ILicenceAuthority,
  ILicenceAuthorityFormValues,
  ILicenceAuthorityPrintoutErrorLog,
  ILicenceAuthorityPrintoutSettings,
  ILicenceAuthorityPrintoutSettingsFormValues,
  LicenceAuthoritySftpConfigDeletionInfoResponse,
} from 'models/licenceAuthority';
import {
  createLicenceAuthority,
  createLicenceAuthorityPrintoutSettings,
  deleteLicenceAuthorityPrintoutSettings,
  getlicenceAuthorities,
  getLicenceAuthority,
  getLicenceAuthorityPrintoutErrorLogData,
  getLicenceAuthorityPrintoutSettings,
  getLicenceAuthoritySftpSendState,
  getLicenceAuthoritySignatureValidate,
  readLicenceAuthorityDeletionInfo,
  resolveLicenceAuthorityPrintoutErrorLogData,
  resumeSendingLicenceAuthorityPrintoutSettings,
  rotatePGPKeyLicenceAuthorityPrintoutSettings,
  rotateSSHKeyLicenceAuthorityPrintoutSettings,
  updateLicenceAuthority,
  updateLicenceAuthorityPrintoutSettings,
} from 'requests/licenceAuthorities';
import { useAlert } from 'models/alertContext';
import { useTranslations } from 'hooks/useTranslations';
import { ActionTypes } from 'state/actions/alert';
import { EAlertVariants } from 'components/atoms/Alert';
import { ILicenceAuthoritySftpConfigInitializationResponse } from './helpers';

export const useLicenceAuthorities = () => {
  const [paginator, setPaginator] = useState<IPaginator | null>(null);
  const [licenceAuthorities, setLicenceAuthorities] = useState<ILicenceAuthority[]>([]);
  const [printoutCanBeResumed, setPrintoutCanBeResumed] = useState(false);
  const [licenceAuthority, setLicenceAuthority] = useState<ILicenceAuthority | null>(null);
  const [printoutSettings, setPrintoutSettings] =
    useState<ILicenceAuthorityPrintoutSettings | null>(null);
  const [printoutDeletionInfo, setPrintoutDeletionInfo] =
    useState<LicenceAuthoritySftpConfigDeletionInfoResponse | null>(null);
  const [printoutErrorLogData, setPrintoutErrorLogData] = useState<
    ILicenceAuthorityPrintoutErrorLog[] | null
  >(null);
  const [paginatorPrintoutErrorLog, setPaginatorPrintoutErrorLog] =
    useState<IPaginator | null>(null);
  const { t } = useTranslations();
  const { setAlert } = useAlert();
  const getLicenceAuthoritiesData = async (
    currentPage: number = 1,
    perPage: EPerPages = EPerPages.perPage25,
    sort?: ISortOptions | null,
  ) => {
    const data = await getlicenceAuthorities(currentPage, perPage, sort, {});

    if (data) {
      const { content, ...paginatorData } = data;
      if (content) {
        setLicenceAuthorities(content);
        setPaginator(paginatorData);
      } else {
        setLicenceAuthorities([]);
        setPaginator(null);
      }
    }
  };
  const getLicenceAuthorityData = async (id: string) => {
    const data = await getLicenceAuthority(id);

    if (data) {
      setLicenceAuthority(data);
      return true;
    }
    setLicenceAuthority(null);
    return null;
  };
  const getLicenceAuthoritySftpSendStateData = async (id: string) => {
    const data = await getLicenceAuthoritySftpSendState(id);
    if (data) {
      setPrintoutCanBeResumed(data.paused);
      return data.paused;
    }
    setPrintoutCanBeResumed(false);
    return false;
  };

  const getSignatureFileValidate = async (file: File) => {
    try {
      const data = await getLicenceAuthoritySignatureValidate(file);
      if (data) {
        return { errorMessage: null, warningMessages: data.warnings };
      }
    } catch (e: any) {
      return { errorMessage: e?.response?.data?.message, warningMessages: null };
    }
    return { errorMessage: null, warningMessages: null };
  };

  const addLicenceAuthority = async (
    body: ILicenceAuthorityFormValues,
  ): Promise<string | null> => {
    try {
      const result = await createLicenceAuthority(body);
      if (result.status === 201) {
        setAlert(ActionTypes.SET_NEXT_PAGE_ALERT, {
          text: t('licenceAuthorities.add.success'),
        });
        return result.data.uniqueId;
      }
    } catch (e: any) {
      setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: e?.response?.data?.message ?? '',
        variant:
          e?.response?.data?.exception === 'LicenceAuthorityCannotDeactivateException'
            ? EAlertVariants.warning
            : EAlertVariants.error,
      });
    }
    return null;
  };
  const editLicenceAuthority = async (
    id: string,
    body: ILicenceAuthorityFormValues,
  ): Promise<boolean> => {
    try {
      const result = await updateLicenceAuthority(id, body);
      if (result.status === 204) {
        setAlert(ActionTypes.SET_NEXT_PAGE_ALERT, {
          text: t('licenceAuthorities.add.success'),
        });
        return true;
      }
    } catch (e: any) {
      setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: e?.response?.data?.message ?? '',
        variant:
          e?.response?.data?.exception === 'LicenceAuthorityCannotDeactivateException'
            ? EAlertVariants.warning
            : EAlertVariants.error,
      });
    }
    return false;
  };

  const getPrintoutSettings = async (id: string) => {
    const data = await getLicenceAuthorityPrintoutSettings(id);
    if (data) {
      setPrintoutSettings(data);
      return true;
    }
    setPrintoutSettings(null);
    return false;
  };

  const addPrintoutSettings = async (
    id: string,
  ): Promise<ILicenceAuthoritySftpConfigInitializationResponse | null> => {
    try {
      const result = await createLicenceAuthorityPrintoutSettings(id);

      if (result) {
        setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
          text: t('licenceAuthoritiesPrintoutSettings.initialization.success'),
        });
        setPrintoutSettings(result);
        return result;
      }
    } catch (e: any) {
      setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: e?.response?.data?.message ?? '',
        variant: EAlertVariants.error,
      });
    }
    return null;
  };
  const editPrintoutSettings = async (
    id: string,
    version: number,
    body: ILicenceAuthorityPrintoutSettingsFormValues,
  ): Promise<boolean> => {
    try {
      const result = await updateLicenceAuthorityPrintoutSettings(id, version, body);
      if (result.status === 200) {
        setAlert(ActionTypes.SET_NEXT_PAGE_ALERT, {
          text:
            version === 0
              ? t('licenceAuthoritiesPrintoutSettings.add.success')
              : t('licenceAuthoritiesPrintoutSettings.savePrintoutSettings.success'),
        });
        return true;
      }
    } catch (e: any) {
      setAlert(ActionTypes.SET_NEXT_PAGE_ALERT, {
        text: e?.response?.data?.message ?? '',
        variant: EAlertVariants.error,
      });
    }
    return false;
  };

  const deletePrintoutSettings = async (id: string, version: number): Promise<boolean> => {
    try {
      const response = await deleteLicenceAuthorityPrintoutSettings(id, version);
      if (response) {
        setAlert(ActionTypes.SET_NEXT_PAGE_ALERT, {
          text: t('licenceAuthoritiesPrintoutSettings.deletePrintoutSettings.success'),
        });
        return true;
      }
    } catch (e: any) {
      setAlert(ActionTypes.SET_NEXT_PAGE_ALERT, {
        text: e?.response?.data?.message ?? '',
        variant: EAlertVariants.error,
      });
    }
    return false;
  };

  const readPrintoutDeletionInfo = async (id: string) => {
    const data = await readLicenceAuthorityDeletionInfo(id);
    if (data) {
      setPrintoutDeletionInfo(data);
      return true;
    }
    setPrintoutDeletionInfo(null);
    return false;
  };

  const getPrintoutErrorLogData = async (
    id: string,
    currentPage: number = 1,
    perPage: EPerPages = EPerPages.perPage25,
  ) => {
    const data = await getLicenceAuthorityPrintoutErrorLogData(id, currentPage, perPage);

    if (data) {
      const { content, ...paginatorData } = data;
      setPrintoutErrorLogData(content);
      setPaginatorPrintoutErrorLog(paginatorData);
      return true;
    }
    setPrintoutErrorLogData(null);
    setPaginatorPrintoutErrorLog(null);
    return null;
  };

  const rotatePGPKeyPrintoutSettings = async (
    id: string,
    version: number,
  ): Promise<boolean> => {
    try {
      await rotatePGPKeyLicenceAuthorityPrintoutSettings(id, version);

      setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: t('licenceAuthoritiesPrintoutSettings.rotatePGPKey.success'),
        variant: EAlertVariants.success,
      });
      return true;
    } catch (e: any) {
      setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: e?.response?.data?.message ?? '',
        variant: EAlertVariants.error,
      });
    }
    return false;
  };

  const rotateSSHKeyPrintoutSettings = async (
    id: string,
    version: number,
  ): Promise<boolean> => {
    try {
      await rotateSSHKeyLicenceAuthorityPrintoutSettings(id, version);

      setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: t('licenceAuthoritiesPrintoutSettings.rotateSSHKey.success'),
        variant: EAlertVariants.success,
      });
      return true;
    } catch (e: any) {
      setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: e?.response?.data?.message ?? '',
        variant: EAlertVariants.error,
      });
    }
    return false;
  };

  const resumeSending = async (id: string) => {
    try {
      await resumeSendingLicenceAuthorityPrintoutSettings(id);
      setAlert(ActionTypes.SET_PRINTOUT_ERROR_LOG_ALERT, {
        text: t('licenceAuthoritiesPrintoutSettings.resume.success'),
        variant: EAlertVariants.success,
      });
      return true;
    } catch (e: any) {
      setAlert(ActionTypes.SET_PRINTOUT_ERROR_LOG_ALERT, {
        text: e?.response?.data?.message ?? '',
        variant: EAlertVariants.error,
      });
    }
    return false;
  };
  const resolvePrintoutErrorLog = async (id: string, alertId: string) => {
    try {
      const data = await resolveLicenceAuthorityPrintoutErrorLogData(id, alertId);
      if (data.status === 200) {
        setAlert(ActionTypes.SET_PRINTOUT_ERROR_LOG_ALERT, {
          text: t('licenceAuthoritiesPrintoutErrorLog.resolve.success'),
          variant: EAlertVariants.success,
        });
        return true;
      }
    } catch (e: any) {
      setAlert(ActionTypes.SET_PRINTOUT_ERROR_LOG_ALERT, {
        text: e?.response?.data?.message ?? '',
        variant: EAlertVariants.error,
      });
    }
    return false;
  };

  return {
    getLicenceAuthoritiesData,
    licenceAuthorities,
    paginator,
    getLicenceAuthorityData,
    licenceAuthority,
    addLicenceAuthority,
    editLicenceAuthority,
    addPrintoutSettings,
    printoutSettings,
    getPrintoutSettings,
    getSignatureFileValidate,
    editPrintoutSettings,
    deletePrintoutSettings,
    printoutDeletionInfo,
    getPrintoutErrorLogData,
    printoutErrorLogData,
    paginatorPrintoutErrorLog,
    readPrintoutDeletionInfo,
    rotatePGPKeyPrintoutSettings,
    rotateSSHKeyPrintoutSettings,
    resumeSending,
    resolvePrintoutErrorLog,
    printoutCanBeResumed,
    getLicenceAuthoritySftpSendStateData,
  };
};
